import { Inventory } from '@mui/icons-material';
import { Button, Stack } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import {
    formatDateString,
    formatDateTimeString,
    IIntake,
    IntakeStatus,
    IParams,
    ISupportPeriod,
    Page,
    PermissionKeys,
    SortOrder,
    Table,
    useHasPermission,
    useQueryParams,
    useSelectedOrganisation,
    Widget,
} from '../../../shared';
import { DuplicatesWarning, SupportCaseTitle } from '../../components';
import { useGetIntakes, useGetSupportCase } from '../../hooks';
import { supportTypesInfoFormatter } from '../../formatters/support-types-info.formatter';

export const SupportCaseIntakesPage: FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { id } = useParams<keyof IParams>() as IParams;
    const { organisation } = useSelectedOrganisation();
    const { hasPermission } = useHasPermission();
    const hasWritePermission = hasPermission(PermissionKeys.INTAKES_WRITE);
    const { page, pageSize, setPage, setPageSize, sort, setSort } = useQueryParams({
        defaultSort: [{ field: 'createdDate', sort: 'desc' }],
    });

    const { data: supportCase } = useGetSupportCase(id);
    const { data: intakes, isPending } = useGetIntakes(id, {
        organisationId: organisation?.id,
        page,
        pageSize,
        sortBy: sort[0].field,
        sortOrder: sort[0].sort?.toUpperCase() as SortOrder,
    });

    const columns: GridColDef[] = [
        {
            field: 'createdDate',
            headerName: t('createdDate'),
            minWidth: 100,
            flex: 1,
            hideable: false,
            valueFormatter: (value: string) => formatDateTimeString(value),
        },
        {
            field: 'externalIntake',
            headerName: t('externalIntakeResult'),
            minWidth: 100,
            flex: 1,
            sortable: false,
            valueFormatter: (value: boolean) => t(value ? 'externalIntake.true' : 'externalIntake.false'),
        },
        {
            field: 'referralOrganisation',
            headerName: t('referralOrganisation'),
            minWidth: 100,
            flex: 1,
            sortable: false,
            valueFormatter: (_, row: IIntake) => row.supportPeriod?.referralOrganisation?.name || '',
        },
        {
            field: 'supportPeriod',
            headerName: t('supportPeriod'),
            minWidth: 150,
            flex: 2,
            sortable: false,
            valueFormatter: (value: ISupportPeriod, row) => {
                if (value?.supportTypesInfo) return supportTypesInfoFormatter(t, value.supportTypesInfo);
                return row?.supportPeriodId ? t('deletedSupportPeriod') : '';
            },
        },
        {
            field: 'supportPeriodStartDate',
            headerName: t('startDateSupportPeriod'),
            minWidth: 150,
            flex: 1,
            sortable: false,
            valueFormatter: (_, row: IIntake) => row?.supportPeriod && formatDateString(row.supportPeriod.startDate),
        },
        {
            field: 'supportPeriodEndDate',
            headerName: t('endDateSupportPeriod'),
            minWidth: 150,
            flex: 1,
            sortable: false,
            valueFormatter: (_, row: IIntake) => row?.supportPeriod && formatDateString(row.supportPeriod.endDate),
        },
        {
            field: 'supportPeriodAppointment',
            headerName: t('appointmentLabel'),
            minWidth: 150,
            flex: 1,
            valueFormatter: (_, row: IIntake) => row?.supportPeriod?.appointment && t(row.supportPeriod.appointment),
        },
        {
            field: 'comment',
            headerName: t('comments'),
            minWidth: 150,
            flex: 2,
            sortable: false,
        },
        {
            field: 'status',
            headerName: t('status'),
            minWidth: 100,
            flex: 1,
            sortable: false,
            valueFormatter: (value: IntakeStatus) => t(value),
        },
    ];

    return (
        <Page onBack={() => navigate(-1)} title={supportCase && <SupportCaseTitle supportCase={supportCase} />}>
            <Stack spacing={2}>
                <DuplicatesWarning supportCaseId={id} />
                <Widget
                    icon={<Inventory color="primary" />}
                    title={t('intakes')}
                    hasTable
                    actions={
                        hasWritePermission && (
                            <Button variant="outlined" color="primary" onClick={() => navigate('new/intro')}>
                                + {t('addIntake')}
                            </Button>
                        )
                    }
                >
                    <Table
                        rows={intakes?.data || []}
                        columns={columns}
                        rowCount={intakes?.pagination?.size || 0}
                        page={page}
                        pageSize={pageSize}
                        setPage={setPage}
                        setPageSize={setPageSize}
                        loading={isPending}
                        sortModel={sort}
                        onSortModelChange={setSort}
                        onRowClick={({ row }) => row.status === IntakeStatus.DRAFT && navigate(`${row.id}/intro`)}
                        prefix="support-case-intakes"
                        disableColumnMenu={false}
                    />
                </Widget>
            </Stack>
        </Page>
    );
};
